// Copyright (c) Docugami, Inc. All rights reserved.

import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";
import { InputState } from "sdv/input/InputController";
import { ActionId } from "sdv/input/Actions";
import { DragComponent } from "sdv/components/DragManipulationComponent";
import { IExternalCallbacks } from "sdv/ExternalCallbacks";
import { InteractableComponent, InteractionType } from "sdv/components/InteractableComponent";
import { getTokenRange } from "sdv/components/ControlComponent";
import { TokenRangeUtils } from "utils/TokenRangeUtils";

/**
 * Manages drag interactions including those on interactables as well
 * as general "drag and drop" events consumed externally.
 */
export class DragSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager;
    input!: InputState;
    callbacks!: IExternalCallbacks;

    queries: Queries = {
        focusedInteractables: { types: [ComponentId.Interactable, ComponentId.CursorHit] },
        activeDrags: { types: [ComponentId.Drag, ComponentId.Interactable] },
        hoverControl: { types: [ComponentId.Control, ComponentId.Hover] },
        selectedControl: { types: [ComponentId.Control, ComponentId.Selected] },
        chunkDrag: { types: [ComponentId.ChunkDrag] }
    }

    update() {
        this.updateEndDrag();
        this.updateStartDrag()
        this.updateChunkDrag();
    }

    private updateStartDrag() {
        const select = this.input.getButton(ActionId.Select);
        if (select.isDown && !select.handled) {
            const entities = this.queries.focusedInteractables.query!.entities;
            if (entities.length > 0) {
                for (const entity of entities) {
                    const interactableComponent = entity.getComponent<InteractableComponent>(ComponentId.Interactable)!;
                    if (interactableComponent.interactionType === InteractionType.Drag && !entity.hasComponent(ComponentId.Drag)) {
                        select.handle();

                        entity.addComponent<DragComponent>({
                            typeId: ComponentId.Drag
                        });

                        if (!!interactableComponent.onInteractStart) {
                            interactableComponent.onInteractStart(entity, this.entities);
                        }
                    }
                }
            }
        }
    }

    private updateEndDrag() {
        const select = this.input.getButton(ActionId.Select);
        if (!select.isPressed || select.isUp) {
            const entities = this.queries.activeDrags.query!.entities;
            entities.forEach(entity => {
                const interactableComponent = entity.getComponent<InteractableComponent>(ComponentId.Interactable)!;
                entity.removeComponent(ComponentId.Drag);
                if (!!interactableComponent.onInteractEnd) {
                    interactableComponent.onInteractEnd(entity, this.entities);
                }
            });
        }
    }

    private updateChunkDrag() {
        const dragEntity = this.queries.chunkDrag.query!.single();
        const hoverEntity = this.queries.hoverControl.query!.single();
        const selectedEntity = this.queries.selectedControl.query!.single();

        if (!hoverEntity) {
            dragEntity?.removeComponent(ComponentId.ChunkDrag);
        }
        else {
            const hoverTokenRange = getTokenRange(hoverEntity)!;
            const selectedTokenRange = getTokenRange(selectedEntity);
            let targetDragEntity: Entity | null = null;
            if (!!selectedTokenRange && TokenRangeUtils.rangeIsContained(hoverTokenRange, selectedTokenRange)) {
                // Ambiguous target - use the selected entity
                targetDragEntity = selectedEntity!;
            }
            else {
                targetDragEntity = hoverEntity;
            }

            if (dragEntity !== targetDragEntity) {
                dragEntity?.removeComponent(ComponentId.ChunkDrag);
            }

            targetDragEntity?.ensureComponent(tagComponent(ComponentId.ChunkDrag));
        }
    }
}
